import { useState } from "react";
import { useCandidateChat } from "@/hooks/useCandidateChat";
import { Sparkle, PaperPlaneTilt } from "@phosphor-icons/react";

function MessageBubble({ role, text, showEllipsis }) {
  const isUser = role === "user";
  return (
    <div className={isUser ? "text-right" : ""}>
      <div
        className={`inline-block max-w-[85%] text-sm px-3 py-2 rounded-lg whitespace-pre-wrap leading-relaxed ${
          isUser
            ? "bg-sky-600 text-white"
            : "bg-slate-50 text-slate-800 border border-slate-100"
        }`}
      >
        {text || (showEllipsis ? "…" : "")}
      </div>
    </div>
  );
}

function MessageList({ messages, streaming }) {
  return (
    <div className="space-y-3 mb-3 max-h-96 overflow-y-auto">
      {messages.map((m, i) => (
        <MessageBubble
          key={m.id}
          role={m.role}
          text={m.text}
          showEllipsis={streaming && i === messages.length - 1 && !m.text}
        />
      ))}
    </div>
  );
}

function ChatInput({ value, onChange, onSend, disabled }) {
  const handleKey = (e) => {
    if (e.key === "Enter") onSend();
  };
  return (
    <div className="flex gap-2">
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKey}
        data-testid="ask-ai-input"
        placeholder="Ask something about this candidate…"
        disabled={disabled}
        className="flex-1 border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40"
      />
      <button
        onClick={onSend}
        disabled={disabled || !value.trim()}
        data-testid="ask-ai-send"
        className="bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white px-3 rounded-md flex items-center gap-1 text-sm"
      >
        <PaperPlaneTilt size={14} /> Send
      </button>
    </div>
  );
}

const EMPTY_HINT = (
  <div className="text-sm text-slate-500 py-4 border border-dashed border-slate-200 rounded-md px-3 mb-3">
    Try: "What should we focus on this week?", "Which companies are missing outreach?", "Draft a follow-up email to a recruiter."
  </div>
);

export default function AskAI({ candidateId }) {
  const [input, setInput] = useState("Summarise this candidate and recommend next actions.");
  const { messages, streaming, ask } = useCandidateChat(candidateId);

  const handleSend = () => {
    ask(input);
    setInput("");
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="ask-ai-panel">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-md bg-sky-600 text-white grid place-items-center">
          <Sparkle size={16} weight="fill" />
        </div>
        <div>
          <h3 className="font-semibold">Ask AI · Candidate Assistant</h3>
          <p className="text-xs text-slate-500">Powered by GPT 5.2 · Grounded on this candidate's data</p>
        </div>
      </div>

      {messages.length === 0 && EMPTY_HINT}
      <MessageList messages={messages} streaming={streaming} />
      <ChatInput value={input} onChange={setInput} onSend={handleSend} disabled={streaming} />
    </div>
  );
}
